<!-- Header Toolbar -->
<app-header-toolbar
  [userDisplayName]="getUserDisplayName()"
  (logout)="onLogout()"
  (back)="onBack()">
</app-header-toolbar>

<div class="page">
  <div class="wrap">

    <!-- ============================ Hero ============================ -->
    <header class="hero">
      <p class="eyebrow">Producer console<span *ngIf="!isNewQuote"> · Editing</span></p>
      <div class="hero-row">
        <h1 class="hero-title">{{ getTitle() }}</h1>
        <span
          class="badge"
          *ngIf="!isNewQuote && quote"
          [ngClass]="getStatusMeta().cls">{{ getStatusMeta().label }}</span>
      </div>
      <p class="hero-sub">{{ getSubtitle() }}</p>
    </header>

    <!-- ================= Flight-path stepper (signature) ================= -->
    <nav class="steps" aria-label="Quote build progress">
      <div class="step" [class.done]="!isNewQuote" [class.current]="isNewQuote">
        <span class="n mono">1</span>Insured &amp; program
      </div>
      <div class="step current">
        <span class="n mono">2</span>Policies &amp; financing
      </div>
      <div class="step">
        <span class="n mono">3</span>Send to customer
      </div>
    </nav>

    <!-- Loading indicator when loading existing quote -->
    <div *ngIf="isLoading" class="state-block">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading quote…</p>
    </div>

    <!-- Form content (hidden while loading) -->
    <form *ngIf="!isLoading" [formGroup]="quoteForm" (ngSubmit)="onSaveQuote()" class="quote-form">
      <mat-tab-group class="quote-tabs">
        <!-- Customer Information Tab -->
        <mat-tab label="Customer Information">
          <div class="tab-content">
            <section class="card">
              <div class="card-head">
                <mat-icon aria-hidden="true">person</mat-icon>
                <h2>Customer details</h2>
              </div>
              <div class="card-body">
                <div class="form-row">
                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Agent Name</mat-label>
                    <input matInput formControlName="agentName" placeholder="Enter agent name">
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Insured Name</mat-label>
                    <input matInput formControlName="insuredName" placeholder="Enter insured name">
                    <mat-error *ngIf="quoteForm.get('insuredName')?.hasError('required')">
                      Insured name is required
                    </mat-error>
                    <mat-error *ngIf="quoteForm.get('insuredName')?.hasError('minlength')">
                      Insured name must be at least 2 characters
                    </mat-error>
                  </mat-form-field>
                </div>

                <div class="form-row">
                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Address Line 1</mat-label>
                    <input matInput formControlName="addressLine1" placeholder="Enter address line 1">
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Address Line 2</mat-label>
                    <input matInput formControlName="addressLine2" placeholder="Enter address line 2">
                  </mat-form-field>
                </div>

                <div class="form-row">
                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>City</mat-label>
                    <input matInput formControlName="city" placeholder="Enter city">
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>State</mat-label>
                    <mat-select formControlName="state">
                      <mat-option *ngFor="let state of stateOptions" [value]="state.value">
                        {{ state.label }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>ZIP Code</mat-label>
                    <input matInput formControlName="postalCode" placeholder="Enter ZIP code">
                  </mat-form-field>
                </div>

                <div class="form-row">
                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Phone</mat-label>
                    <input matInput formControlName="phone" placeholder="Enter phone number" (input)="formatPhoneNumber($event)" (blur)="formatPhoneOnBlur($event)">
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Email</mat-label>
                    <input matInput type="email" formControlName="email" placeholder="Enter email address">
                    <mat-error *ngIf="quoteForm.get('email')?.hasError('email')">
                      Please enter a valid email address
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Customer Number</mat-label>
                    <input matInput formControlName="customerNumber" placeholder="Enter customer number">
                  </mat-form-field>
                </div>
              </div>
            </section>

            <!-- Quote Configuration -->
            <section class="card">
              <div class="card-head">
                <mat-icon aria-hidden="true">settings</mat-icon>
                <h2>Quote configuration</h2>
              </div>
              <div class="card-body">
                <div class="form-row">
                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Loan Type</mat-label>
                    <mat-select formControlName="loanType">
                      <mat-option *ngFor="let option of loanTypeOptions" [value]="option.value">
                        {{ option.label }}
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="quoteForm.get('loanType')?.hasError('required')">
                      Loan type is required
                    </mat-error>
                  </mat-form-field>
                </div>

                <div class="form-row">
                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Program</mat-label>
                    <mat-select formControlName="programId">
                      <mat-option *ngFor="let program of programs" [value]="program.id">
                        {{ program.name }} ({{ program.rateTiers.length || 0 }} tier{{ (program.rateTiers.length || 0) !== 1 ? 's' : '' }})
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="quoteForm.get('programId')?.hasError('required')">
                      Program is required
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="ifq-field">
                    <mat-label>Insurance Company</mat-label>
                    <mat-select formControlName="insuranceCompanyId">
                      <mat-option [value]="null">None</mat-option>
                      <mat-option *ngFor="let company of insuranceCompanies" [value]="company.id">
                        {{ company.name }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>

              </div>
            </section>
          </div>
        </mat-tab>

        <!-- Policies Tab -->
        <mat-tab label="Policies">
          <div class="tab-content">
            <!-- Policy Edit Notice for existing quotes -->
            <div *ngIf="!isNewQuote" class="notice">
              <mat-icon aria-hidden="true">info</mat-icon>
              <span>Policy updates are not currently supported. You can view and modify quote information, but policy changes will not be saved. To modify policies, please create a new quote.</span>
            </div>

            <section class="card">
              <div class="card-head">
                <mat-icon aria-hidden="true">description</mat-icon>
                <h2>Schedule of policies</h2>
                <span class="card-badge" *ngIf="policiesFormArray.length">
                  {{ policiesFormArray.length }} coverage{{ policiesFormArray.length !== 1 ? 's' : '' }}
                </span>
              </div>

              <div class="card-body">
                <div class="policies-list" formArrayName="policies">
                  <div
                    *ngFor="let policy of policiesFormArray.controls; let i = index"
                    [formGroupName]="i"
                    class="policy-card">

                    <div class="policy-header">
                      <h4><span class="policy-idx mono">{{ i + 1 }}</span> Policy {{ i + 1 }}</h4>
                      <button
                        type="button"
                        mat-icon-button
                        (click)="removePolicy(i)"
                        *ngIf="policiesFormArray.length > 1 && isNewQuote"
                        class="remove-policy-button"
                        aria-label="Remove policy">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>

                    <div class="form-row">
                      <mat-form-field appearance="outline" class="ifq-field">
                        <mat-label>Policy Number</mat-label>
                        <input matInput formControlName="policyNumber" placeholder="Enter policy number">
                        <mat-error *ngIf="policy.get('policyNumber')?.hasError('required')">
                          Policy number is required
                        </mat-error>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="ifq-field">
                        <mat-label>Coverage</mat-label>
                        <mat-select formControlName="coverage">
                          <mat-option *ngFor="let option of coverageOptions" [value]="option.value">
                            {{ option.label }}
                          </mat-option>
                        </mat-select>
                        <mat-error *ngIf="policy.get('coverage')?.hasError('required')">
                          Coverage is required
                        </mat-error>
                      </mat-form-field>
                    </div>

                    <div class="form-row">
                      <mat-form-field appearance="outline" class="ifq-field">
                        <mat-label>Effective Date</mat-label>
                        <input matInput [matDatepicker]="effectivePicker" formControlName="effectiveDate">
                        <mat-datepicker-toggle matSuffix [for]="effectivePicker"></mat-datepicker-toggle>
                        <mat-datepicker #effectivePicker></mat-datepicker>
                        <mat-error *ngIf="policy.get('effectiveDate')?.hasError('required')">
                          Effective date is required
                        </mat-error>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="ifq-field">
                        <mat-label>Expiration Date</mat-label>
                        <input matInput [matDatepicker]="expirationPicker" formControlName="expirationDate">
                        <mat-datepicker-toggle matSuffix [for]="expirationPicker"></mat-datepicker-toggle>
                        <mat-datepicker #expirationPicker></mat-datepicker>
                      </mat-form-field>
                    </div>

                    <div class="form-row">
                      <mat-form-field appearance="outline" class="ifq-field ifq-field--figure">
                        <mat-label>Premium Amount</mat-label>
                        <input matInput type="number" formControlName="premiumAmount" placeholder="0.00">
                        <span matPrefix>$&nbsp;</span>
                        <mat-error *ngIf="policy.get('premiumAmount')?.hasError('required')">
                          Premium amount is required
                        </mat-error>
                        <mat-error *ngIf="policy.get('premiumAmount')?.hasError('min')">
                          Premium amount must be positive
                        </mat-error>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="ifq-field ifq-field--figure">
                        <mat-label>Taxes Amount</mat-label>
                        <input matInput type="number" formControlName="taxesAmount" placeholder="0.00">
                        <span matPrefix>$&nbsp;</span>
                        <mat-error *ngIf="policy.get('taxesAmount')?.hasError('required')">
                          Taxes amount is required
                        </mat-error>
                        <mat-error *ngIf="policy.get('taxesAmount')?.hasError('min')">
                          Taxes amount must be positive
                        </mat-error>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="ifq-field ifq-field--figure">
                        <mat-label>Fees Amount</mat-label>
                        <input matInput type="number" formControlName="feesAmount" placeholder="0.00">
                        <span matPrefix>$&nbsp;</span>
                        <mat-error *ngIf="policy.get('feesAmount')?.hasError('required')">
                          Fees amount is required
                        </mat-error>
                        <mat-error *ngIf="policy.get('feesAmount')?.hasError('min')">
                          Fees amount must be positive
                        </mat-error>
                      </mat-form-field>
                    </div>

                    <div class="form-row">
                      <mat-form-field appearance="outline" class="ifq-field ifq-field--figure">
                        <mat-label>Min Earned Percent</mat-label>
                        <input matInput type="number" formControlName="minEarnedPercent" placeholder="0.00">
                        <span matSuffix>%</span>
                        <mat-error *ngIf="policy.get('minEarnedPercent')?.hasError('min')">
                          Min earned percent must be positive
                        </mat-error>
                        <mat-error *ngIf="policy.get('minEarnedPercent')?.hasError('max')">
                          Min earned percent cannot exceed 100%
                        </mat-error>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="ifq-field ifq-field--figure">
                        <mat-label>Min Earned Amount</mat-label>
                        <input matInput type="number" formControlName="minEarnedAmount" placeholder="0.00">
                        <span matPrefix>$&nbsp;</span>
                        <mat-error *ngIf="policy.get('minEarnedAmount')?.hasError('min')">
                          Min earned amount must be positive
                        </mat-error>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="ifq-field ifq-field--figure">
                        <mat-label>Down Amount</mat-label>
                        <input matInput type="number" formControlName="downAmount" placeholder="0.00" [value]="policy.get('downAmount')?.value">
                        <span matPrefix>$&nbsp;</span>
                        <mat-error *ngIf="policy.get('downAmount')?.hasError('min')">
                          Down amount must be positive
                        </mat-error>
                      </mat-form-field>
                    </div>

                    <div class="form-row">
                      <mat-form-field appearance="outline" class="ifq-field">
                        <mat-label>Cancel Days</mat-label>
                        <mat-select formControlName="cancelDays">
                          <mat-option *ngFor="let option of cancelDaysOptions" [value]="option.value">
                            {{ option.label }}
                          </mat-option>
                        </mat-select>
                        <mat-error *ngIf="policy.get('cancelDays')?.hasError('required')">
                          Cancel days is required
                        </mat-error>
                      </mat-form-field>
                    </div>

                    <div class="form-row checkbox-row">
                      <mat-checkbox formControlName="auditable">Auditable</mat-checkbox>
                      <mat-checkbox formControlName="lossPayeeRequired">Loss Payee Required</mat-checkbox>
                      <mat-checkbox formControlName="lossPayeeRequest">Loss Payee Request</mat-checkbox>
                    </div>
                  </div>

                  <div *ngIf="policiesFormArray.length === 0" class="no-policies">
                    <mat-icon>description</mat-icon>
                    <p>No policies added yet. Add a coverage line to get started.</p>
                  </div>

                  <button
                    type="button"
                    (click)="addPolicy()"
                    class="btn-add"
                    [disabled]="!isNewQuote">
                    <mat-icon aria-hidden="true">add</mat-icon>
                    Add coverage line
                  </button>
                </div>

                <!-- Running financing totals (mono figures) -->
                <div class="totals" *ngIf="policiesFormArray.length">
                  <div class="line">
                    <span class="k">Total premium</span>
                    <span class="v mono">{{ formatWhole(getTotalPremium()) }}</span>
                  </div>
                  <div class="line">
                    <span class="k">Total down payment</span>
                    <span class="v mono">{{ formatWhole(getTotalDown()) }}</span>
                  </div>
                  <div class="line total">
                    <span class="k">Amount financed</span>
                    <span class="v mono">{{ formatWhole(getAmountFinanced()) }}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </mat-tab>
      </mat-tab-group>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button
          type="button"
          (click)="onResetForm()"
          class="btn-ghost reset-button">
          <mat-icon aria-hidden="true">refresh</mat-icon>
          Reset Form
        </button>

        <button
          type="button"
          (click)="onCancel()"
          class="btn-ghost cancel-button">
          <mat-icon aria-hidden="true">cancel</mat-icon>
          Cancel
        </button>

        <button
          type="submit"
          class="cta save-button"
          [disabled]="quoteForm.invalid || isSaving || policiesFormArray.length === 0">
          <mat-spinner *ngIf="isSaving" diameter="20" class="button-spinner"></mat-spinner>
          <mat-icon *ngIf="!isSaving" aria-hidden="true">{{ isNewQuote ? 'add' : 'save' }}</mat-icon>
          {{ getSubmitButtonText() }}
        </button>
      </div>
    </form>
  </div>
</div>
